import { Text, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Box, ThemeProvider } from "@mui/material";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useGetShift } from "@src/appV2/Shifts/Shift/api/useGetShift";
import { parseISO } from "date-fns";
import { useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { formatTimeRange } from "../../utils/formatTimeRange";
import { CreateShiftVerificationConfirmationBottomSheet } from "./ConfirmationBottomSheet";
import { ShiftVerificationRequestForm } from "./FormComponents/Form";
import { CreateShiftVerificationRequestSkeleton } from "./PageLoadingSkeleton";

interface RouteParams {
  shiftId: string;
}

export function ShiftVerificationRequestPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const scrollRef = useRef<HTMLDivElement>(null);
  const confirmationModalState = useModalState();

  const {
    data: shiftData,
    isLoading: isShiftLoading,
    isError: isShiftError,
    isSuccess,
  } = useGetShift(shiftId, {
    enabled: isDefined(shiftId) && shiftId.length > 0,
  });

  const shift = shiftData?.response;

  const isLoading = isShiftLoading;
  const isError = isShiftError || (isSuccess && !isDefined(shift));

  const formattedShiftTimeRange = isDefined(shift)
    ? formatTimeRange(
        { startDate: parseISO(shift.start), endDate: parseISO(shift.end) },
        { timezone: shift.facility.tmz }
      )
    : undefined;

  const formattedShiftDay = isDefined(shift)
    ? formatDayOfMonthWithDayName(parseISO(shift.start), { timezone: shift.facility.tmz })
    : undefined;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          isLoading={isLoading}
          title="Shift verification request"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
        />

        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            flexGrow: 1,
          }}
        >
          {isLoading && <CreateShiftVerificationRequestSkeleton />}
          {isError && <Alert severity="error">Error loading shift verification details.</Alert>}
          {isSuccess && isDefined(shift) && (
            <>
              <Box px={5} pb={6}>
                <Text variant="body1" sx={{ mb: 2 }}>
                  Request payment from {shift.facility.name} for your {shift.name.toUpperCase()}{" "}
                  shift on {formattedShiftDay} ({formattedShiftTimeRange})
                </Text>
              </Box>
              <ShiftVerificationRequestForm
                shiftId={shiftId}
                shift={shift}
                onSuccess={() => {
                  confirmationModalState.openModal();
                }}
              />
            </>
          )}
        </Box>

        <CreateShiftVerificationConfirmationBottomSheet
          modalState={confirmationModalState}
          onContinue={() => {
            history.push(DeprecatedGlobalAppV1Paths.MY_SHIFTS);
          }}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
